import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpDown,
  Briefcase,
  Calendar,
  CalendarDays,
  Check,
  ChevronDown,
  Circle,
  Clock,
  Flag,
  Plus,
  List,
  ListFilter,
  Search,
  User,
  Users,
  X,
  Zap,
} from "lucide-react";
import { DEADLINE_FILTERS, NONE, PRIORITIES } from "./constants";
import { statusesFor } from "./boardState";

// Priority swatches (UI only).
const PRIORITY_COLORS = { critical: "#EF4444", high: "#FF8A1F", medium: "#FACC15", low: "#78746E" };

const hueOf = (name = "") => [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
const initialsOf = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

/** Colour square or initials avatar shown before an option label. */
const Lead = ({ option }) => {
  if (option.avatar) {
    return (
      <span
        className="w-[22px] h-[22px] rounded-full shrink-0 inline-flex items-center justify-center text-[10px] font-semibold text-white"
        style={{ background: `hsl(${hueOf(option.label)} 55% 45%)` }}
      >
        {initialsOf(option.label)}
      </span>
    );
  }
  if (option.swatch || option.swatchClass) {
    return (
      <span
        className={`w-[9px] h-[9px] rounded-[3px] shrink-0 ${option.swatchClass || ""}`}
        style={option.swatch ? { background: option.swatch } : undefined}
      />
    );
  }
  return null;
};

/**
 * Inline dropdown: the menu opens below the trigger inside the panel so it is
 * never clipped. Picking the selected option again clears it.
 */
const Dropdown = ({ label, value, options, placeholder, open, onToggle, onSelect }) => {
  const selected = options.find((o) => o.value === value);
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className={`w-full h-[34px] px-2.5 rounded-[10px] border text-[13px] inline-flex items-center gap-2 text-left transition-colors ${
          selected ? "bg-accent-soft border-accent-line text-ink" : "bg-panel2 border-line text-muted hover:bg-hover"
        }`}
      >
        {selected && <Lead option={selected} />}
        <span className="flex-1 truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="listbox" className="mt-1.5 p-[5px] rounded-xl bg-panel2 border border-line2 max-h-[220px] overflow-y-auto">
          {/* "All" = no filter, same as resetting this one */}
          <button
            type="button"
            role="option"
            aria-selected={!selected}
            onClick={() => onSelect("")}
            className={`w-full h-[34px] px-2.5 rounded-lg text-[13px] text-ink inline-flex items-center gap-2 text-left ${
              !selected ? "bg-accent-soft" : "hover:bg-raise"
            }`}
          >
            <span className="flex-1 truncate">{placeholder}</span>
            {!selected && <Check className="w-3.5 h-3.5 text-accent shrink-0" />}
          </button>
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => onSelect(active ? "" : o.value)}
                className={`w-full h-[34px] px-2.5 rounded-lg text-[13px] text-ink inline-flex items-center gap-2 text-left ${
                  active ? "bg-accent-soft" : "hover:bg-raise"
                }`}
              >
                <Lead option={o} />
                <span className="flex-1 truncate">{o.label}</span>
                {active && <Check className="w-3.5 h-3.5 text-accent shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SORTS = [
  { value: "manual", label: "Manual", Icon: List },
  { value: "deadline", label: "Due date", Icon: Clock },
  { value: "priority", label: "Priority", Icon: Flag },
  { value: "created", label: "Created", Icon: Plus },
];

/**
 * Task Board toolbar: Search, Filters panel and Sort menu. All options come
 * from /options (database); values are ids. The search box is debounced so
 * typing does not refetch on every keystroke. Filter / sort state and URL
 * params are owned by the page (`filters`, `view`, `onChange`, `onViewChange`).
 */
const BoardFilters = ({ filters, view = {}, options, visible, onChange, onViewChange, onClear, matchCount }) => {
  const [q, setQ] = useState(filters.q || "");
  const [panelOpen, setPanelOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [dir, setDir] = useState(view.sort?.endsWith("-desc") ? "desc" : "asc");
  const searchRef = useRef(null);

  useEffect(() => setQ(filters.q || ""), [filters.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.q || "") !== q) onChange("q", q);
    }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // "/" focuses the search box; Esc closes the open menus.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPanelOpen(false);
        setSortOpen(false);
      } else if (e.key === "/" && !/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) && !e.target.isContentEditable) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (view.sort) setDir(view.sort.endsWith("-desc") ? "desc" : "asc");
  }, [view.sort]);

  const named = (list, extra = {}) => list.map((x) => ({ value: x._id, label: x.name, ...extra(x) }));
  const withNone = (list, label, allow) => (allow ? [{ value: NONE, label }, ...list] : list);

  const projectOptions = withNone(
    named(options.projects, (p) => ({ swatch: p.color || "#78746E" })),
    "No project",
    true,
  );
  const sprintOptions = withNone(named(options.sprints, () => ({})), "No sprint", true);
  const squadOptions = named(options.squads, () => ({}));
  const assigneeOptions = withNone(
    options.assignees.map((a) => ({ value: a._id, label: a.fullNameEn || a.nickname, avatar: true })),
    "Unassigned",
    // Only unplanned (admin-only) backlog tasks can be unassigned.
    options.access.isAdmin,
  );
  const statusOptions = statusesFor(options.access).map((s) => ({ value: s.value, label: s.label, swatchClass: s.accent }));
  const priorityOptions = PRIORITIES.map((p) => ({ value: p.value, label: p.label, swatch: PRIORITY_COLORS[p.value] }));

  const rows = [
    { key: "sprint", label: "Sprint", Icon: Zap, placeholder: "All sprints", options: sprintOptions },
    { key: "project", label: "Project", Icon: Briefcase, placeholder: "All projects", options: projectOptions },
    visible.squad && { key: "squad", label: "Squad", Icon: Users, placeholder: "All squads", options: squadOptions },
    visible.assignee && { key: "assignee", label: "Assignee", Icon: User, placeholder: "Anyone", options: assigneeOptions },
    { key: "status", label: "Status", Icon: Circle, placeholder: "Any status", options: statusOptions },
    { key: "priority", label: "Priority", Icon: Flag, placeholder: "Any priority", options: priorityOptions },
    { key: "deadline", label: "Deadline", Icon: Calendar, placeholder: "Any time", options: DEADLINE_FILTERS, isView: true },
  ].filter(Boolean);

  const valueOf = (row) => (row.isView ? view[row.key] : filters[row.key]) || "";
  const setValue = (row, v) => (row.isView ? onViewChange(row.key, v) : onChange(row.key, v));

  const active = useMemo(
    () => Object.values(filters).filter(Boolean).length + [view.deadline, view.sort, view.due].filter(Boolean).length,
    [filters, view],
  );

  // Chips for every set filter (the search text is already visible in its box).
  const chips = [
    ...rows
      .filter((r) => valueOf(r))
      .map((r) => ({
        id: r.key,
        label: r.label,
        value: r.options.find((o) => o.value === valueOf(r))?.label || "…",
        remove: () => setValue(r, ""),
      })),
    view.due && {
      id: "due",
      label: "Due date",
      value: new Date(`${view.due}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }),
      remove: () => onViewChange("due", ""),
    },
    filters.q && { id: "q", label: "Search", value: filters.q, remove: () => onChange("q", "") },
  ].filter(Boolean);

  const sortMode = view.sort ? view.sort.split("-")[0] : "manual";
  const sortLabel = SORTS.find((s) => s.value === sortMode)?.label || "Manual";
  const pickSort = (mode) => {
    onViewChange("sort", mode === "manual" ? "" : `${mode}-${dir}`);
    setSortOpen(false);
  };
  const pickDir = (d) => {
    setDir(d);
    if (view.sort) onViewChange("sort", `${sortMode}-${d}`);
  };

  const toolbarBtn =
    "h-[38px] px-[13px] rounded-[11px] border text-[13.5px] font-medium inline-flex items-center gap-2 whitespace-nowrap transition-colors";

  return (
    <div>
      <div className="relative flex flex-wrap items-center gap-2" style={{ containerType: "inline-size" }}>
        {/* Search */}
        <label className="flex items-center gap-2 h-[38px] px-3 rounded-[11px] bg-panel border border-line flex-[1_1_220px] max-w-[320px] focus-within:border-accent-line focus-within:shadow-[0_0_0_4px_var(--accent-soft)]">
          <Search className="w-[15px] h-[15px] text-faint shrink-0" />
          <input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks…"
            aria-label="Search tasks by title"
            className="flex-1 min-w-0 bg-transparent outline-none text-[13.5px] text-ink placeholder:text-faint"
          />
          <kbd className="font-mono text-[11px] text-faint border border-line2 rounded-md px-1.5 leading-[18px]">/</kbd>
        </label>

        {/* Filters button */}
        <button
          type="button"
          onClick={() => {
            setPanelOpen((o) => !o);
            setSortOpen(false);
            setOpenRow(null);
          }}
          aria-expanded={panelOpen}
          className={`${toolbarBtn} ${
            panelOpen || active > 0 ? "bg-accent-soft border-accent-line text-ink" : "bg-panel border-line text-ink hover:bg-hover"
          }`}
        >
          <ListFilter className="w-4 h-4" />
          Filters
          {active > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-grad text-on-accent text-[11px] font-bold inline-flex items-center justify-center">
              {active}
            </span>
          )}
        </button>

        {/* Sort button + menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setSortOpen((o) => !o);
              setPanelOpen(false);
            }}
            aria-expanded={sortOpen}
            className={`${toolbarBtn} ${sortOpen ? "bg-raise" : "bg-panel"} border-line text-ink hover:bg-hover`}
          >
            <ArrowUpDown className="w-4 h-4" />
            <span className="text-muted">Sort</span>
            <span>{sortLabel}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </button>
          {sortOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
              <div className="absolute right-0 top-[46px] z-[41] w-60 p-1.5 rounded-[14px] bg-panel-solid border border-line2 shadow-[0_30px_60px_-24px_rgba(0,0,0,.55)] animate-ws-in">
                {SORTS.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => pickSort(value)}
                    className={`w-full h-9 px-2.5 rounded-[9px] text-[13px] text-ink inline-flex items-center gap-2.5 ${
                      sortMode === value ? "bg-raise" : "hover:bg-hover"
                    }`}
                  >
                    <Icon className="w-4 h-4 text-muted" />
                    <span className="flex-1 text-left">{label}</span>
                    {sortMode === value && <Check className="w-3.5 h-3.5 text-accent" />}
                  </button>
                ))}
                <div className="my-1.5 border-t border-line" />
                <div className="flex p-[3px] rounded-[10px] bg-raise">
                  {[
                    ["asc", "Ascending"],
                    ["desc", "Descending"],
                  ].map(([d, label]) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => pickDir(d)}
                      className={`flex-1 h-7 rounded-lg text-[12.5px] ${
                        dir === d ? "bg-panel-solid text-ink shadow-card" : "text-muted hover:text-ink"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filters panel */}
        {panelOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setPanelOpen(false)} />
            <div
              className="absolute left-0 top-[46px] z-[41] rounded-[18px] bg-panel-solid border border-line2 shadow-[0_30px_60px_-24px_rgba(0,0,0,.55)] animate-ws-in overflow-hidden"
              style={{ width: "min(560px, 100cqw)" }}
            >
              <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-line">
                <span className="font-display text-[15px] font-semibold text-ink">Filter tasks</span>
                {typeof matchCount === "number" && (
                  <span className="text-xs text-faint">
                    {matchCount} task{matchCount === 1 ? "" : "s"} match
                  </span>
                )}
              </div>
              <div className="px-[18px] py-1.5 overflow-y-auto" style={{ maxHeight: "min(460px, 60vh)" }}>
                {rows.map((row) => (
                  <div key={row.key} className="grid grid-cols-[96px_1fr] gap-x-3 py-3 border-b border-line last:border-b-0">
                    <span className="inline-flex items-center gap-2 text-[12.5px] text-muted h-[34px]">
                      <row.Icon className="w-3.5 h-3.5" /> {row.label}
                    </span>
                    <Dropdown
                      label={row.label}
                      value={valueOf(row)}
                      options={row.options}
                      placeholder={row.placeholder}
                      open={openRow === row.key}
                      onToggle={() => setOpenRow((cur) => (cur === row.key ? null : row.key))}
                      onSelect={(v) => {
                        setValue(row, v);
                        setOpenRow(null);
                      }}
                    />
                  </div>
                ))}
                <div className="grid grid-cols-[96px_1fr] gap-x-3 py-3">
                  <span className="inline-flex items-center gap-2 text-[12.5px] text-muted h-[34px]">
                    <CalendarDays className="w-3.5 h-3.5" /> Due date
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={view.due || ""}
                      onChange={(e) => onViewChange("due", e.target.value)}
                      aria-label="Filter by due date"
                      className={`flex-1 min-w-0 h-[34px] px-2.5 rounded-[10px] border text-[13px] outline-none ${
                        view.due ? "bg-accent-soft border-accent-line text-ink" : "bg-panel2 border-line text-muted"
                      }`}
                    />
                    {view.due && (
                      <button
                        type="button"
                        onClick={() => onViewChange("due", "")}
                        aria-label="Clear due date"
                        className="w-[34px] h-[34px] rounded-[10px] inline-flex items-center justify-center text-muted hover:bg-hover"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-[18px] py-3 bg-panel2 border-t border-line">
                <button type="button" onClick={onClear} className="text-[13px] text-muted hover:text-ink">
                  Reset all
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Active filters */}
      {chips.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2 animate-ws-in">
          {chips.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-full bg-accent-soft border border-accent-line text-[12.5px]"
            >
              <span className="text-muted">{c.label}</span>
              <span className="font-bold text-accent-ink max-w-[160px] truncate">{c.value}</span>
              <button
                type="button"
                onClick={c.remove}
                aria-label={`Remove ${c.label} filter`}
                className="w-5 h-5 rounded-full inline-flex items-center justify-center hover:bg-hover"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={onClear} className="text-[12.5px] text-faint hover:text-ink">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default BoardFilters;
